import { useEffect, useRef, useState } from 'react';
import { EditInput } from './styles';

const pad2 = (n: number) => String(n).padStart(2, '0');

const secondsToHMS = (totalSeconds: number) => {
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = totalSeconds % 60;
  return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
};

const SEGMENTS = [
  { start: 0, end: 2, max: 99 },
  { start: 3, end: 5, max: 59 },
  { start: 6, end: 8, max: 59 },
];

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const getSegmentIndexFromCaret = (caret: number) => {
  if (caret <= 2) return 0;
  if (caret <= 5) return 1;
  return 2;
};

const setSegmentValue = (hms: string, index: number, value: number) => {
  const { start, end, max } = SEGMENTS[index];
  const segment = pad2(clamp(value, 0, max));
  return `${hms.slice(0, start)}${segment}${hms.slice(end)}`;
};

const isValidHMS = (value: string, { maxHour = 99, strictFull = true } = {}) => {
  const match = /^(\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (!match) return !strictFull;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  const ss = Number(match[3]);
  if (hh < 0 || hh > maxHour) return false;
  if (mm < 0 || mm > 59) return false;
  if (ss < 0 || ss > 59) return false;
  return true;
};

const hmsToSeconds = (value: string) => {
  const [hh, mm, ss] = value.split(':').map(Number);
  return hh * 3600 + mm * 60 + ss;
};

type Props = {
  value: number;
  onChange: (valueSeconds: number) => void;
};

const DurationInput = ({ value, onChange }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const typedDigitRef = useRef<{ segmentIndex: number; digit: string } | null>(null);
  const isFocusedRef = useRef(false);

  const [timerText, setTimerText] = useState(() => secondsToHMS(value));

  useEffect(() => {
    if (isFocusedRef.current) return;
    setTimerText(secondsToHMS(value));
  }, [value]);

  const selectSegment = (segmentIndex: number) => {
    const input = inputRef.current;
    if (!input) return;

    const { start, end } = SEGMENTS[segmentIndex];
    input.setSelectionRange(start, end);
  };

  const moveToSegment = (segmentIndex: number) => {
    const nextIndex = clamp(segmentIndex, 0, 2);
    requestAnimationFrame(() => {
      selectSegment(nextIndex);
    });
  };

  const emitIfValid = (nextValue: string) => {
    if (isValidHMS(nextValue)) {
      onChange(hmsToSeconds(nextValue));
    }
  };

  return (
    <EditInput
      ref={inputRef}
      type="text"
      inputMode="numeric"
      placeholder="00:00:00"
      maxLength={8}
      onFocus={() => {
        isFocusedRef.current = true;
        typedDigitRef.current = null;
        moveToSegment(0);
      }}
      onClick={(e) => {
        typedDigitRef.current = null;
        const target = e.currentTarget;
        const caret = target.selectionStart ?? 0;
        moveToSegment(getSegmentIndexFromCaret(caret));
      }}
      onKeyDown={(e) => {
        const target = e.currentTarget;
        const caret = target.selectionStart ?? 0;
        const segmentIndex = getSegmentIndexFromCaret(caret);

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          typedDigitRef.current = null;
          moveToSegment(segmentIndex - 1);
          return;
        }

        if (e.key === 'ArrowRight' || e.key === ':' || e.key === 'Tab') {
          e.preventDefault();
          typedDigitRef.current = null;
          moveToSegment(segmentIndex + 1);
          return;
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          typedDigitRef.current = null;
          const nextValue = setSegmentValue(timerText, segmentIndex, 0);
          setTimerText(nextValue);
          emitIfValid(nextValue);
          moveToSegment(segmentIndex);
          return;
        }

        if (/^\d$/.test(e.key)) {
          e.preventDefault();

          const pending = typedDigitRef.current;
          const isSecondDigit = pending?.segmentIndex === segmentIndex;

          if (!isSecondDigit) {
            const nextValue = setSegmentValue(timerText, segmentIndex, Number(e.key));
            typedDigitRef.current = { segmentIndex, digit: e.key };
            setTimerText(nextValue);
            emitIfValid(nextValue);
            moveToSegment(segmentIndex);
            return;
          }

          const doubleDigit = Number(`${pending.digit}${e.key}`);
          const nextValue = setSegmentValue(timerText, segmentIndex, doubleDigit);
          typedDigitRef.current = null;
          setTimerText(nextValue);
          emitIfValid(nextValue);
          moveToSegment(segmentIndex + 1);
          return;
        }

        if (e.key.length === 1) {
          e.preventDefault();
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const hh = Number(raw.slice(0, 2) || 0);
        const mm = Number(raw.slice(2, 4) || 0);
        const ss = Number(raw.slice(4, 6) || 0);
        const nextValue = `${pad2(clamp(hh, 0, 99))}:${pad2(clamp(mm, 0, 59))}:${pad2(clamp(ss, 0, 59))}`;
        typedDigitRef.current = null;
        setTimerText(nextValue);
        emitIfValid(nextValue);
        moveToSegment(2);
      }}
      onBlur={() => {
        isFocusedRef.current = false;
        typedDigitRef.current = null;
        if (isValidHMS(timerText)) {
          onChange(hmsToSeconds(timerText));
        }
      }}
      value={timerText}
      onChange={() => {
        // Controlled through keyboard handlers to keep segment-based behavior.
      }}
    />
  );
};

export default DurationInput;
